/**
 * MyLocationMarker — Google Maps-style blue dot showing user's own GPS position.
 * Renders inside <GoogleMap> as a child component.
 * Uses browser Geolocation API directly (bypasses gpsService settings gate).
 * Uploads pings to worker_pings via mapService.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Marker, Circle } from '@react-google-maps/api';
import { uploadGpsPing } from '../../services/mapService';
import { findNearestSegment } from '../../services/gpsService';

function buildBlueDotSvg() {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'>
    <circle cx='12' cy='12' r='10' fill='rgba(66,133,244,0.2)' stroke='none'/>
    <circle cx='12' cy='12' r='7' fill='#4285F4' stroke='white' stroke-width='2.5'/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export default function MyLocationMarker({ loggedInUser, segments, projectId, onPositionUpdate, mapRef }) {
  const [position, setPosition] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);
  const bestAccuracyRef = useRef(Infinity);

  const handlePosition = useCallback((pos) => {
    const lat = pos.coords.latitude;
    const lng = pos.coords.longitude;
    const acc = pos.coords.accuracy;

    // Only accept if accuracy is better than 5km (reject IP-based fallbacks)
    // or if it improves on what we already have
    if (acc > 5000 && bestAccuracyRef.current < acc) return;

    if (acc < bestAccuracyRef.current) bestAccuracyRef.current = acc;

    setPosition({ lat, lng });
    setAccuracy(acc);
    setError(null);

    // Upload ping to DB (only when accuracy is within ~10 feet / 3 meters)
    if (loggedInUser?.email && acc < 3) {
      const nearest = findNearestSegment({ lat, lng }, segments || []);
      uploadGpsPing({
        worker_email: loggedInUser.email,
        project_id: projectId || null,
        lat,
        lng,
        accuracy: acc,
        speed: pos.coords.speed,
        heading: pos.coords.heading,
        nearest_segment_id: nearest?.segment_id || null,
        nearest_segment_distance: nearest?.distance || null,
        timestamp: new Date(pos.timestamp).toISOString(),
      });
    }

    if (onPositionUpdate) onPositionUpdate({ lat, lng, accuracy: acc });
  }, [loggedInUser, segments, projectId, onPositionUpdate]);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation not supported');
      return;
    }

    // watchPosition continuously refines: IP → WiFi → GPS as sources become available
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      (err) => {
        console.warn('[MyLocationMarker] watchPosition error:', err.message);
        setError(err.code === 1 ? 'Location permission denied' : 'Location unavailable');
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!position || !window.google) return null;

  const blueDotIcon = {
    url: buildBlueDotSvg(),
    scaledSize: new window.google.maps.Size(24, 24),
    anchor: new window.google.maps.Point(12, 12),
  };

  return (
    <>
      {/* Accuracy ring */}
      {accuracy && accuracy > 10 && (
        <Circle
          center={position}
          radius={accuracy}
          options={{
            fillColor: '#4285F4',
            fillOpacity: 0.1,
            strokeColor: '#4285F4',
            strokeOpacity: 0.3,
            strokeWeight: 1,
            clickable: false,
            zIndex: 500,
          }}
        />
      )}
      {/* Blue dot */}
      <Marker
        position={position}
        icon={blueDotIcon}
        title={`Your location (±${accuracy > 1000 ? Math.round(accuracy / 1000) + 'km' : Math.round(accuracy) + 'm'})`}
        zIndex={1500}
      />
    </>
  );
}

/**
 * GpsToggleButton — Floating button rendered OUTSIDE <GoogleMap>, on top of the map container.
 * Tap to toggle GPS on/off. When turning on, pans map to user's location.
 * Blue when active, gray when off.
 */
export function GpsToggleButton({ darkMode, mapRef, gpsOn, onToggle }) {
  const [locating, setLocating] = useState(false);

  const handleClick = useCallback(() => {
    if (gpsOn) {
      // Turn off
      onToggle(false);
      return;
    }
    // Turn on — also pan to location
    onToggle(true);
    if (!('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const map = mapRef?.current || mapRef;
        if (map?.panTo) {
          map.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          if (map.getZoom() < 16) map.setZoom(16);
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [gpsOn, onToggle, mapRef]);

  const active = gpsOn || locating;

  return (
    <button
      onClick={handleClick}
      title={gpsOn ? 'Turn off GPS' : 'Turn on GPS'}
      style={{
        position: 'absolute',
        bottom: 80,
        right: 10,
        zIndex: 1000,
        width: 40,
        height: 40,
        borderRadius: 8,
        border: active ? '2px solid #4285F4' : '2px solid transparent',
        background: active
          ? (darkMode ? 'rgba(66,133,244,0.2)' : 'rgba(66,133,244,0.1)')
          : (darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.95)'),
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#4285F4' : (darkMode ? '#ccc' : '#333')} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
      </svg>
    </button>
  );
}
